import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import LivingActionPlan from "@/components/summary/LivingActionPlan";
import { getAssessment, getAccess } from "@/lib/api";

export default function ActionPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState({});
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, acc] = await Promise.all([getAssessment(id), getAccess(id)]);
        setReadiness(a.readiness || {});
        setAccess(acc);
      } catch (e) {
        console.error(e);
        toast.error("Could not load your action plan.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 lg:px-10 py-10">
        <Link
          to={`/summary/${id}`}
          data-testid="plan-back-to-summary"
          className="inline-flex items-center gap-2 h-10 px-3 -ml-3 rounded-md text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Summary
        </Link>

        {loading ? (
          <div className="mt-12 flex justify-center" data-testid="plan-loading">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : access?.paid_managed ? (
          <div className="mt-6" data-testid="focused-action-plan">
            <LivingActionPlan assessmentId={id} readiness={readiness} />
          </div>
        ) : (
          <div className="mt-10 rounded-xl border border-teal-200 bg-teal-50/50 p-8 text-center" data-testid="plan-locked">
            <h1 className="font-display text-2xl font-semibold text-slate-900">Your Living Action Plan is part of Managed Resilience</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-lg mx-auto">
              Upgrade to assign owners, track progress against your score, export to Excel and get a quarterly re-score.
            </p>
            <button
              onClick={() => navigate("/pricing")}
              data-testid="plan-upgrade-cta"
              className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors"
            >
              <Lock className="h-4 w-4" /> Upgrade to Managed
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
